import { useTranslation } from '@/i18n/useTranslation';

import { shiftLine } from '../shift';
import type { JobSummary } from '../types';

export interface JobCardProps {
  readonly job: JobSummary;
  readonly onOpen: (job: JobSummary) => void;
}

/**
 * One opening, as a tappable card [KJM-3; Figma `06 · Jobs` — 12021:40669 featured, 12021:40670 ordinary].
 *
 * **A `<button>`, not the frame's `<a>`.** Opening a card navigates nowhere: it moves the kiosk's own flow
 * state. An anchor with no href is unreachable by keyboard and announces itself as a link to somewhere,
 * which on a device with no browser chrome is a promise the screen cannot keep (§ 9, native elements first).
 *
 * **The card presents; it never decides** [KJM-3 § Invariant]. `featured` changes only the border and adds a
 * badge — it never moves the card, because the backend already resolved the pin against the radius-filtered
 * set and this component renders the flag it was given [decision 0062].
 *
 * Values read from the iPad frame and confirmed unchanged at 1440 and 375 — only the card's own width moves:
 * 448 inside the content column at both card widths, 327 at mobile. Padding 16, gap 8, radius 8; the
 * ordinary card a 1px `--divider-border` hairline, the featured one a 2px `#f5a821`.
 */
export function JobCard({ job, onOpen }: JobCardProps) {
  const t = useTranslation();
  const shift = shiftLine(job);

  /*
   * The second line is `client · city`, and either half can be missing. Joined here rather than in the
   * markup so that one absent half does not leave a dangling separator on a lobby screen — the frame draws
   * "Ridgeline Fulfillment · Bethlehem, PA" and has no state for half of it.
   */
  const place = [job.clientName, job.city]
    .filter((part) => part !== null && part !== '')
    .join(' · ');

  return (
    <button
      className={`kiosk-focusable flex w-full flex-col items-start gap-2 rounded-[var(--radius-sm)] bg-[var(--card)] p-4 text-left ${
        job.featured
          ? 'border-2 border-solid border-[#f5a821]'
          : 'border border-solid border-[var(--divider-border)]'
      }`}
      onClick={() => onOpen(job)}
      type="button"
    >
      {job.featured && (
        /*
         * 10px bold uppercase on `#fdf1df` with `#8a5a06` text — a pairing that clears 4.5:1, unlike the
         * brand amber the convention skill § 9 flags. `tracking-[0.2px]` is the frame's RENDERED value; the
         * style sheet's raw number is a percentage of the font size (see `Button`).
         */
        <span className="rounded-full bg-[#fdf1df] px-2 py-[2px] text-[10px] font-bold uppercase leading-3 tracking-[0.2px] text-[#8a5a06]">
          {t('jobs.featured')}
        </span>
      )}

      <span className="w-full text-base font-semibold leading-6 text-[var(--foreground)]">
        {job.title ?? t('jobs.untitled')}
      </span>

      {place !== '' && (
        <span className="text-sm leading-5 text-[var(--muted-foreground)]">{place}</span>
      )}

      {/*
        The meta row: shift and pay in body weight, the job type muted beside them. `flex-wrap` is a
        departure from the frame, which draws one line — at 375 the column is 327px and three items at 14px
        overflow it. Wrapping is the only option that does not shrink the text below the frame's size or clip
        a pay rate, and it is the same choice the arrival screen's option rows make.
      */}
      <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm leading-5">
        {shift !== null && <span className="font-bold text-[var(--foreground)]">{shift}</span>}
        {job.payRate !== null && (
          <span className="font-bold text-[var(--foreground)]">{job.payRate}</span>
        )}
        {job.jobType !== null && (
          <span className="text-[var(--muted-foreground)]">{job.jobType}</span>
        )}
      </span>
    </button>
  );
}
